import React, { useEffect, useState } from 'react';
import PropTypes from "prop-types";
import { useNavigate } from 'react-router-dom';

import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax
import 'mapbox-gl/dist/mapbox-gl.css';
mapboxgl.accessToken = 'pk.eyJ1IjoiaGFycnltb250YW5vIiwiYSI6ImNqZWY0bDcyaTFkcGYzM28ydHFpaDJxaWcifQ.Wf7aNVePBRWZ9fMy_I81qw';
import { getUserAccessLevel } from 'utils/setup';
import { setGroupPolygonColour, setDevicePointColour } from 'utils/map';


function setGroupFeatures(group, user_type) {
  const { geojson, ...metadata } = group;

  var group_features = geojson;

  group_features.forEach((feature) => {
    feature.properties = {
      ...metadata,
      access: getUserAccessLevel(user_type, 'technician'),
      feature_colour: setGroupPolygonColour(group, user_type)
    };
  });

  return group_features;
}

function setDeviceFeatures(device, user_type) {
  return {
    type: "Feature",
    properties: {
      ...device,
      access: getUserAccessLevel(user_type, 'technician'),
      ...setDevicePointColour(device, user_type)
    },
    geometry: {
      type: 'Point',
      coordinates: device.position
    }
  }
}

function ClearwaysGroupsMap({ map, user, groups_data, setGroupPreviewID, showDevices }) {

  const navigate = useNavigate();

  const popup = new mapboxgl.Popup({
    closeButton: false,
    closeOnClick: false
  });

  const handleGroupClick = (e) => {
    e.preventDefault();
    setGroupPreviewID(e.features[0].properties.group_id);
    navigate("/overview/" + e.features[0].properties.group_id);
  }

  const handleDeviceClick = (e) => {
    e.preventDefault();
    popup.remove();
    navigate("/devices/" + e.features[0].properties.uuid);
  }

  const handleGroupMouseEnter = (e) => {
    map.current.getCanvas().style.cursor = 'pointer';
  }

  const handleGroupMouseLeave = (e) => {
    map.current.getCanvas().style.cursor = '';
  }

  const handleDeviceMouseEnter = (e) => {
    map.current.getCanvas().style.cursor = 'pointer';

    const coordinates = e.features[0].geometry.coordinates.slice();
    var description = `<div style='text-align:center; font-size:16px'><b>${e.features[0].properties.site_id}</b></div>`;
    if (getUserAccessLevel(user.user.user_type, 'technician')) {
      description = `<div style='text-align:center; font-size:16px'><b>${e.features[0].properties.site_id}</b><br><div style='font-size:13px'>${e.features[0].properties.name}</div></div>`;
    }

    while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
      coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
    }

    popup.setLngLat(coordinates).setHTML(description).addTo(map.current);
  }

  const handleDeviceMouseLeave = (e) => {
    map.current.getCanvas().style.cursor = '';
    popup.remove();
  }

  useEffect(() => {  //mount
    if (!map.current) return;

    var existing_device_source = map.current.getSource('devices-source');
    var existing_device_layer = map.current.getLayer('devices-layer');

    var existing_group_source = map.current.getSource('groups-source');
    var existing_group_layer = map.current.getLayer('groups-layer');

    if (!existing_device_source) {
      map.current.addSource('devices-source', {
        type: 'geojson',
        data: {
          "type": "FeatureCollection",
          "features": groups_data.flatMap(group => group.devices).map(device => setDeviceFeatures(device, user.user.user_type)).sort((a, b) => { return a.properties.zIndex - b.properties.zIndex })
        }
      });
    }

    if (!existing_device_layer) {
      map.current.addLayer({
        id: 'devices-layer',
        type: 'circle',
        source: 'devices-source',
        paint: {
          'circle-color': ['get', 'feature_colour'],
          'circle-radius': 9,
          'circle-stroke-width': 3,
          'circle-stroke-color': '#ffffff'
        }
      });
    }

    const processedFeatures = groups_data.flatMap((group) => setGroupFeatures(group, user.user.user_type));

    if (!existing_group_source) {
      map.current.addSource('groups-source', {
        type: 'geojson',
        data: {
          "type": "FeatureCollection",
          "features": processedFeatures
        }
      });
    }
    else {
      existing_group_source.setData({
        "type": "FeatureCollection",
        "features": processedFeatures
      });
    }

    if (!existing_group_layer) {
      map.current.addLayer({
        id: 'groups-layer',
        type: 'fill',
        source: 'groups-source',
        paint: {
          'fill-color': ['get', 'feature_colour'],
          'fill-opacity': 0.2,
        }
      }).addLayer({
        id: 'groups-layer-liner',
        type: 'line',
        source: 'groups-source',
        paint: {
          "line-color": ['get', 'feature_colour'],
          "line-width": 2
        }
      });
    }

    map.current.on('click', 'devices-layer', handleDeviceClick);
    map.current.on('mouseenter', 'devices-layer', handleDeviceMouseEnter);
    map.current.on('mouseleave', 'devices-layer', handleDeviceMouseLeave);

    map.current.on('click', 'groups-layer', handleGroupClick);
    map.current.on('mouseenter', 'groups-layer', handleGroupMouseEnter);
    map.current.on('mouseleave', 'groups-layer', handleGroupMouseLeave);

    return () => {
      map.current.off('click', 'groups-layer', handleGroupClick);
      map.current.off('mouseenter', 'groups-layer', handleGroupMouseEnter);
      map.current.off('mouseleave', 'groups-layer', handleGroupMouseLeave);

      map.current.off('click', 'devices-layer', handleDeviceClick);
      map.current.off('mouseenter', 'devices-layer', handleDeviceMouseEnter);
      map.current.off('mouseleave', 'devices-layer', handleDeviceMouseLeave);
    }
  }, [showDevices, groups_data]);

  useEffect(() => {
    if (!map.current) return;

    map.current.setFilter('devices-layer', showDevices ? ['all'] : ['==', ['id'], '']);
    map.current.setFilter('groups-layer', !showDevices ? ['all'] : ['==', ['id'], '']);
    map.current.setFilter('groups-layer-liner', !showDevices ? ['all'] : ['==', ['id'], '']);

  }, [showDevices]);

  useEffect(() => () => { //unmount
    map.current.removeLayer('groups-layer-liner');
    map.current.removeLayer('groups-layer');
    map.current.removeSource('groups-source');

    map.current.removeLayer('devices-layer');
    map.current.removeSource('devices-source');

    map.current.getCanvas().style.cursor = '';
    popup.remove();
  }, []);
}

ClearwaysGroupsMap.propTypes = {
  map: PropTypes.object,
  user: PropTypes.object,
  groups_data: PropTypes.array,
  setGroupPreviewID: PropTypes.func,
  showDevices: PropTypes.bool,
};

export default ClearwaysGroupsMap;